import { useState } from 'react';
import { createPortal } from 'react-dom';
import { Globe, AlertTriangle, CheckCircle, XCircle } from 'lucide-react';
import { useAdminOperatorBranding, useUpdateOperatorDomainLifecycle } from '../../api/hooks';
import { Spinner } from '../../components/ui';
import { fmtDateTime } from '../../lib/utils';
import { toast } from '../../store';
import {
  DOMAIN_ACTIONS,
  DOMAIN_ACTION_ORDER,
  DOMAIN_ROUTING_NOTICE,
  DOMAIN_STATUS_LABELS,
  DOMAIN_VERIFY_NOTICE,
  availableDomainActions,
  domainBrandingLoadErrorMessage,
  domainLifecycleErrorMessage,
} from './operatorDomainLifecycle';

type DomainAction = keyof typeof DOMAIN_ACTIONS;

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <p className="text-txt-muted text-2xs font-mono uppercase tracking-widest">{label}</p>
      <div className="font-mono text-txt-secondary mt-0.5 break-words">{children}</div>
    </div>
  );
}

function approverLabel(approverId: unknown, knownUsers: any[]) {
  if (!Number.isInteger(approverId)) return '—';
  const match = knownUsers.find((u: any) => u?.id === approverId);
  const name = match ? (match.full_name || match.username) : null;
  return name ? `${name} (#${approverId})` : `User #${approverId}`;
}

function ConfirmDomainActionDialog({
  action,
  domain,
  acknowledged,
  onAcknowledge,
  onConfirm,
  onCancel,
  isPending,
  error,
}: {
  action: DomainAction | null;
  domain: string;
  acknowledged: boolean;
  onAcknowledge: (value: boolean) => void;
  onConfirm: () => void;
  onCancel: () => void;
  isPending: boolean;
  error: string;
}) {
  if (!action) return null;
  const meta = DOMAIN_ACTIONS[action];
  const needsAck = Boolean(meta.acknowledgement);
  const node = (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="domain-action-title"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
    >
      <div className="card w-full max-w-md animate-fade-in shadow-panel flex flex-col max-h-[calc(100dvh-2rem)] my-auto">
        <div className="p-6 pb-3 shrink-0">
          <h3 id="domain-action-title" className="font-display font-semibold text-txt-primary">{meta.title}</h3>
          <p className="text-2xs font-mono text-txt-muted mt-0.5 break-all">{domain}</p>
        </div>
        <div className="px-6 overflow-y-auto space-y-3">
          <p className="text-xs text-txt-secondary leading-relaxed">{meta.body}</p>
          {action === 'verify' && (
            <p className="text-xs text-warn bg-warn/10 border border-warn/30 rounded px-3 py-2">{DOMAIN_VERIFY_NOTICE}</p>
          )}
          {needsAck && (
            <label className="flex items-start gap-2 cursor-pointer text-xs text-txt-secondary">
              <input
                type="checkbox"
                checked={acknowledged}
                onChange={(e) => onAcknowledge(e.target.checked)}
                disabled={isPending}
                className="accent-amber mt-0.5"
              />
              <span>{meta.acknowledgement}</span>
            </label>
          )}
          {error && (
            <p role="alert" className="text-2xs text-bear bg-bear/10 border border-bear/30 rounded px-3 py-2">{error}</p>
          )}
        </div>
        <div className="p-6 pt-4 shrink-0 flex gap-2 justify-end">
          <button type="button" onClick={onCancel} disabled={isPending} className="btn-ghost text-xs">
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={isPending || (needsAck && !acknowledged)}
            className={`${meta.tone === 'danger' ? 'btn-danger' : 'btn-primary'} text-xs flex items-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed`}
          >
            {isPending && <Spinner size={12} />}
            Confirm {meta.label}
          </button>
        </div>
      </div>
    </div>
  );
  if (typeof document === 'undefined') return node;
  return createPortal(node, document.body);
}

// Super Admin-only custom-domain management for one Operator. The parent
// decides visibility; the backend independently enforces Super Admin access.
// `operatorId` must be the id of the authorized, loaded admin user record.
export function OperatorCustomDomainSection({
  operatorId,
  knownUsers = [],
}: {
  operatorId: number;
  knownUsers?: any[];
}) {
  const branding = useAdminOperatorBranding(operatorId);
  const lifecycle = useUpdateOperatorDomainLifecycle();
  const [pendingAction, setPendingAction] = useState<DomainAction | null>(null);
  const [acknowledged, setAcknowledged] = useState(false);
  const [dialogError, setDialogError] = useState('');

  const data: any = branding.data;
  // Fail closed if the response is not this exact Operator's record (e.g. a
  // stale render while switching between users).
  const matches = data != null && data.operator_id === operatorId;
  const allowed = matches ? availableDomainActions(data, operatorId) : new Set<string>();
  const busy = lifecycle.isPending;

  const closeDialog = () => {
    if (busy) return;
    setPendingAction(null);
    setAcknowledged(false);
    setDialogError('');
  };

  const openDialog = (action: DomainAction) => {
    if (busy || !allowed.has(action)) return;
    setAcknowledged(false);
    setDialogError('');
    setPendingAction(action);
  };

  const confirmAction = async () => {
    if (!pendingAction || busy) return;
    const meta = DOMAIN_ACTIONS[pendingAction];
    if (!allowed.has(pendingAction)) {
      setDialogError('This action is no longer available for the current domain status.');
      return;
    }
    if (meta.acknowledgement && !acknowledged) return;
    setDialogError('');
    try {
      await lifecycle.mutateAsync({ operatorId, action: pendingAction });
      toast.success(meta.success);
      setPendingAction(null);
      setAcknowledged(false);
    } catch (err: any) {
      setDialogError(domainLifecycleErrorMessage(err));
    }
  };

  const status: string = matches ? data.domain_status : '';
  const routingEnabled = matches && data.domain_routing_enabled === true;

  return (
    <div className="panel p-5 space-y-3" data-testid="custom-domain-management">
      <div className="flex items-center gap-2">
        <Globe size={14} className="text-amber" />
        <h3 className="text-xs font-mono text-txt-muted uppercase tracking-widest">Custom Domain Management</h3>
      </div>

      {branding.isLoading ? (
        <div className="flex items-center gap-2 text-xs text-txt-muted"><Spinner size={12} /> Loading…</div>
      ) : branding.error ? (
        <p role="alert" className="text-xs text-bear flex items-center gap-2">
          <AlertTriangle size={13} className="text-warn" />
          {domainBrandingLoadErrorMessage(branding.error)}
        </p>
      ) : !matches ? (
        <p className="text-xs text-txt-muted">Custom domain settings are unavailable for this Operator.</p>
      ) : (
        <>
          <div className="grid grid-cols-2 gap-3 text-xs">
            <Field label="Business Name">
              {data.business_name || '—'}
              {data.is_default && <span className="block text-2xs text-txt-muted font-sans">AlgoSys default (no branding saved)</span>}
            </Field>
            <Field label="Proposed Domain">{data.proposed_domain || 'None proposed'}</Field>
            <Field label="Domain Status">
              <span className="inline-flex px-2 py-0.5 rounded border text-2xs font-mono border-bg-border">
                {DOMAIN_STATUS_LABELS[status as keyof typeof DOMAIN_STATUS_LABELS] ?? 'Unknown'}
              </span>
            </Field>
            <Field label="Domain Routing">
              <span className={`flex items-center gap-1 ${routingEnabled ? 'text-bull' : 'text-txt-muted'}`}>
                {routingEnabled ? <><CheckCircle size={11} /> Enabled</> : <><XCircle size={11} /> Disabled</>}
              </span>
            </Field>
            <Field label="Verified At">{data.domain_verified_at ? fmtDateTime(data.domain_verified_at) : '—'}</Field>
            <Field label="Approved At">{data.domain_approved_at ? fmtDateTime(data.domain_approved_at) : '—'}</Field>
            <Field label="Approved By">{approverLabel(data.domain_approved_by_user_id, knownUsers)}</Field>
          </div>

          <div className="space-y-2 text-2xs text-txt-muted leading-relaxed">
            <p className="flex items-start gap-1.5">
              <AlertTriangle size={11} className="text-warn mt-0.5 shrink-0" />
              <span>{DOMAIN_VERIFY_NOTICE}</span>
            </p>
            <p className="flex items-start gap-1.5">
              <AlertTriangle size={11} className="text-warn mt-0.5 shrink-0" />
              <span>{DOMAIN_ROUTING_NOTICE} Approval never enables routing automatically.</span>
            </p>
          </div>

          <div className="pt-2 border-t border-bg-border flex flex-wrap gap-2">
            {(DOMAIN_ACTION_ORDER as readonly DomainAction[]).map((action) => {
              const enabled = allowed.has(action) && !busy;
              const meta = DOMAIN_ACTIONS[action];
              return (
                <button
                  key={action}
                  type="button"
                  data-action={action}
                  onClick={() => openDialog(action)}
                  disabled={!enabled}
                  className={`text-xs px-3 py-1.5 rounded border transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${
                    meta.tone === 'danger'
                      ? 'border-bear/40 text-bear hover:bg-bear/10'
                      : 'border-amber/40 text-amber hover:bg-amber/10'
                  }`}
                >
                  {meta.label}
                </button>
              );
            })}
          </div>
          {allowed.size === 0 && (
            <p className="text-2xs text-txt-muted">
              {data.proposed_domain
                ? 'No domain actions are available for the current status.'
                : 'The Operator has not proposed a custom domain yet.'}
            </p>
          )}
        </>
      )}

      <ConfirmDomainActionDialog
        action={pendingAction}
        domain={matches ? data.proposed_domain || '' : ''}
        acknowledged={acknowledged}
        onAcknowledge={setAcknowledged}
        onConfirm={confirmAction}
        onCancel={closeDialog}
        isPending={busy}
        error={dialogError}
      />
    </div>
  );
}

export default OperatorCustomDomainSection;
